import $ from 'jquery';
import _ from 'underscore';
import autosize from 'lib/autosize';
import wafer from 'lib/wafer';
import ajax from 'util/ajax';
import { track } from 'util/analytics';
import * as autosave from 'util/autosave';
import { CLASS_NAME_ACTIVE } from 'util/constants';
import * as deferred from 'util/deferred';
import { $body, $document, $window, jqGetElemById, isMobile, setScrollPosition } from 'util/global';
import * as latex from 'util/latex';
import sticky from 'util/sticky';
import { Anchors, getScrollHeight, showConfirmationModal } from 'util/ui';


/** @module */
const Wiki = {};


let checkWikiHashTimeout;
let $outdatedWikiModal;
let $saveOutdatedWikiModal;


var checkWikiHash = function ($editor) {
    if (!$outdatedWikiModal) {
        $outdatedWikiModal = jqGetElemById('outdated-wiki-modal');
    }

    _.defer(function () {
        $editor.find('textarea').on('input', _.once(function () {
            var data = $editor.data(),
                autosaveKey = data.autosaveUser + '_' + data.autosaveKey,
                savedHash = autosave.getEditHash(autosaveKey);

            var poll = function () {
                $.get(data.hashUrl).then(function (response) {
                    // the wiki has been updated
                    if(response.summary_hash && response.summary_hash != savedHash) {
                        // show outdated wiki modal
                        $outdatedWikiModal.modal();
                        // remove from localstorage
                        autosave.discardEdit(autosaveKey);
                        // stop polling
                        clearTimeout(checkWikiHashTimeout);
                    }
                });
                checkWikiHashTimeout = setTimeout(poll, 30000);
            };

            poll();
        }));
    });

    $outdatedWikiModal.find('.discard-btn').on('click', function () {
        // reload the page
        window.location.reload();
    });
};

var saveWiki = function ($editor) {
    var $form = $editor.parents('form'),
        $previewBtn = $form.find('.preview-button');

    const saveHandler = () => {
        if ($form.hasClass('summary-text-editor')) {
            $('.wiki-main-column').removeClass('editing');
        }
        // since "save" button triggers a confirmation modal sometimes, rather than using
        // "autosave-submit" class on the button, manually remove edits from localstorage
        // not before asynchronous operations are completed
        autosave.discardEdit(autosaveKey);
        $previewBtn.val('');
        $form.submit();
    };

    if (checkWikiHashTimeout) {
        if (!$saveOutdatedWikiModal) {
            $saveOutdatedWikiModal = jqGetElemById('save-outdated-wiki-modal');
        }

        // stop polling for current wiki hash
        clearTimeout(checkWikiHashTimeout);

        var data = $editor.data(),
            autosaveKey = data.autosaveUser + '_' + data.autosaveKey,
            savedHash = autosave.getEditHash(autosaveKey);

        // get current wiki hash one more time
        $.get(data.hashUrl).then((response) => {
            // the wiki has been updated
            if (response.summary_hash && response.summary_hash !== savedHash) {
                // show save outdated wiki modal
                $saveOutdatedWikiModal.modal();
            } else {
                saveHandler();
            }
        });

        $saveOutdatedWikiModal.find('.save-btn').on('click', saveHandler);
    } else {
        saveHandler();
    }
};


/** @param {JQuery} $sticky */
const editorButtonsScrollHandler = ($sticky) => {
    if ($sticky.hasClass('stuck')) {
        const $parent = $sticky.parent();
        $sticky.css({
            width: $parent.width(),
            left: $parent.offset().left - $document.scrollLeft(),
            margin: 0,
        });
    } else {
        $sticky.css({
            width: '',
            left: '',
            margin: '',
        });
    }
};


/**
 * Edit Wiki button click handler
 * @param {JQuery} $button
 */
Wiki.editSummary = ($button) => {
    let clickHandler;

    if ($button.data('is-logged-out')) {
        clickHandler = () => {
            $('.signup-modal').removeClass('hide');
        };
    } else {
        clickHandler = () => {
            $('.wiki-main-column').addClass('editing');

            const data = $('.edit-summary-container').data();
            ajax(data.pageKey).get(data.url, {
                component_key: data.componentKey,
            });
        };

        // for after saving
        latex.updateMathjax($('.summary'));
    }

    $button.on('click', clickHandler);
};

var checkInputTiming = function ($textarea, formType) {
    // how many inputs to use in the rolling average
    var inputTimingLimit = 30;
    // the avg input timing in ms required to send info to mixpanel
    var inputTimingThreshold = 30;

    var inputTimings = [];
    var timingsTracked = false;
    var lastTime;

    var average = function (arr) {
        var sum = 0;
        arr.forEach(function (a) {
            sum += a;
        });
        return sum / arr.length;
    };

    $textarea.on('keydown.timecheck', function () {
        lastTime = (new Date()).getTime();
    });

    $textarea.on('input.timecheck', () => {
        if (lastTime) {
            const now = (new Date()).getTime();

            if (inputTimings.length >= inputTimingLimit) {
                inputTimings.shift();
            }
            inputTimings.push(now - lastTime);

            if (!timingsTracked && inputTimings.length >= inputTimingLimit) {
                const avgTimings = average(inputTimings);
                if (avgTimings >= inputTimingThreshold) {
                    timingsTracked = true;
                    $textarea.off('.timecheck');
                    track('ui', {
                        element_id: 'input_timings_wiki',
                        path: window.location.pathname,
                        input_timing_avg: Math.round(avgTimings),
                        input_timing_content_length: $textarea.val().length,
                        input_timing_form_type: formType,
                        input_timing_threshold: inputTimingThreshold,
                    });
                }
            }
        }
    });
};

/**
 * Summary editor form event handlers
 * @param {JQuery} $form
 */
Wiki.editSummaryForm = function ($form) {
    const formData = $form.data();
    const { isPreview, hasErrors, isUnchanged } = formData;
    const $wiki = $('.wiki-main-column');
    const $editor = jqGetElemById('summary_text_editor');
    const $editorButtons = $editor.find('.button-container');
    const $textarea = $editor.find('textarea');
    const $preview = jqGetElemById('summary_text_preview');
    const $buttonContainer = $preview.find('.button-container');
    const $previewBtn = $form.find('.preview-button');
    const $cancelBtns = $form.find('.cancel-button');
    const $editBtn = $form.find('.edit-button');
    const $saveBtns = $form.find('.save-button');

    const onload = () => {
        autosize($textarea);
        sticky.stick($editorButtons, {
            bottom: $editorButtons.offset().top + $form.outerHeight(),
            handler: editorButtonsScrollHandler,
        });

        checkInputTiming($textarea, 'wiki_summary');
    };

    const previewSummary = () => {
        setScrollPosition(0);
        latex.updateMathjax($preview);

        setTimeout(() => {
            sticky.stick($buttonContainer, {
                bottom: $buttonContainer.offset().top + $form.outerHeight(),
                handler: editorButtonsScrollHandler,
            });
        }, 100); // wait for mathjax updates
    };

    if (!isPreview && !hasErrors && !isUnchanged) {
        onload();
        checkWikiHash($editor);
    } else if (isPreview) {
        previewSummary();
    } else if (isUnchanged) {
        $wiki.removeClass('editing');
    }

    $previewBtn.on('click', () => {
        $saveBtns.val('');
    });

    $cancelBtns.on('click', (e) => {
        e.preventDefault();
        if (isPreview) {
            showConfirmationModal({ title: formData.cancelConfirmText })
                .then((confirmed) => {
                    if (confirmed) {
                        $wiki.removeClass('editing');
                    }
                });
        } else {
            $wiki.removeClass('editing');
        }
    });

    $editBtn.on('click', () => {
        $form.addClass('editing');
        setScrollPosition(0);
        onload();
    });

    $saveBtns.on('click', (e) => {
        e.preventDefault();
        if (!isPreview) {
            showConfirmationModal({ title: formData.saveConfirmText })
                .then((confirmed) => {
                    if (confirmed) {
                        saveWiki($editor);
                    }
                });
        } else {
            saveWiki($editor);
        }
    });
};

/**
 * Edit Section button click handler
 * @param {JQuery} $button
 */
Wiki.editSection = function ($button) {
    const buttonData = $button.data();
    const buttonId = buttonData.id;
    const buttonHeading = buttonData.heading;
    const $sidebar = jqGetElemById('wiki-sidebar');
    const $section = jqGetElemById(`section-${buttonId}`);
    const $sectionEdit = $section.find('.edit-section-container');
    const data = $sectionEdit.data();
    const $modal = jqGetElemById('edit-another-section-modal');

    var showEditor = function() {
        if ($section.length && $sidebar.length) {
            // hide sidebar when editing
            var sidebarBottom = $sidebar.offset().top + $sidebar.outerHeight();
            if ($section.offset().top < sidebarBottom) {
                $sidebar.addClass('hide');
            }
        }

        $section.addClass('editing');
        $sectionEdit.addClass('cmp_' + data.componentKey + '_id');

        ajax(data.pageKey).get(data.url, {
            component_key: data.componentKey,
            heading: buttonHeading
        });
    };

    $button.on('click', () => {
        // already editing another section
        const $editSection = $('.cmp_wiki_canonical_edit_section_id');
        if ($editSection.length) {
            const currentHeading = $editSection.find('#id_old_heading').val();
            const tmpl = _.template(jqGetElemById('discard-changes-text').text());

            $modal.find('.modal-body').html(tmpl({
                currentHeading,
                buttonHeading,
            }));

            $modal.modal();

            $modal.find('.discard-btn').on('click', () => {
                const $currentEditor = $('.cmp_wiki_canonical_edit_section_id');
                const $currentSection = $currentEditor.parent('.section');
                const $currentHeading = $currentSection.prev('.skill-heading');

                $currentEditor.removeClass('cmp_wiki_canonical_edit_section_id');
                $currentSection.removeClass('editing');
                $currentHeading.removeClass('hide');
                showEditor();
            });
        } else {
            showEditor();
        }
    });
};

/**
 * Section editor form event handlers
 * @param {JQuery} $form
 */
Wiki.editSectionForm = function ($form) {
    var formData = $form.data(),
        isPreview = formData.isPreview,
        isUnchanged = formData.isUnchanged,
        $section = $form.parents('.section'),
        $sectionHeading = $section.prev('.skill-heading'),
        $editContainer = $form.parent('.edit-section-container'),
        $editor = $form.find('.section-text-editor'),
        $editorButtons = $form.children('.button-container'),
        $editorField = $editor.find('.autosave-field'),
        $textarea = $editor.find('textarea'),
        $preview = $form.find('.section-preview'),
        $buttonContainer = $preview.find('.button-container'),
        $previewBtn = $form.find('.preview-button'),
        $cancelBtns = $form.find('.cancel-button'),
        $editBtn = $form.find('.edit-button'),
        $saveBtns = $form.find('.save-button');

    const onload = () => {
        $section.addClass('editing');
        $sectionHeading.addClass('hide');
        autosize($textarea);
        sticky.stick($editorButtons, {
            bottom: $editorButtons.offset().top + $form.outerHeight(),
            handler: editorButtonsScrollHandler,
        });
        checkInputTiming($textarea, 'wiki_section');
    };

    const previewSection = () => {
        latex.updateMathjax($preview);

        setTimeout(() => {
            sticky.stick($buttonContainer, {
                bottom: $buttonContainer.offset().top + $form.outerHeight(),
                handler: editorButtonsScrollHandler,
            });
        }, 100); // wait for mathjax updates
    };

    const hideEditor = () => {
        $editContainer.removeClass('cmp_wiki_canonical_edit_section_id');
        $section.removeClass('editing');
        $sectionHeading.removeClass('hide');
    };

    if (!isPreview && !isUnchanged) {
        onload();
        checkWikiHash($editorField);
    } else if (isPreview) {
        previewSection();
    } else if (isUnchanged) {
        hideEditor();
    }

    $previewBtn.on('click', () => {
        $saveBtns.val('');
        $form.removeClass('editing');
    });

    $cancelBtns.on('click', (e) => {
        e.preventDefault();
        if (isPreview) {
            showConfirmationModal({ title: formData.cancelConfirmText })
                .then((confirmed) => {
                    if (confirmed) {
                        hideEditor();
                    }
                });
        } else {
            hideEditor();
        }
    });

    $editBtn.on('click', () => {
        $form.addClass('editing');
        autosize($textarea);
        sticky.stick($editorButtons, {
            bottom: $editorButtons.offset().top + $form.outerHeight(),
            handler: editorButtonsScrollHandler,
        });
    });

    $saveBtns.on('click', (e) => {
        e.preventDefault();
        if (!isPreview) {
            showConfirmationModal({ title: formData.saveConfirmText })
                .then((confirmed) => {
                    if (confirmed) {
                        saveWiki($editorField);
                    }
                });
        } else {
            saveWiki($editorField);
        }
    });
};


/**
 * Expands and collapses sections on mobile
 * @param {JQuery} $section
 */
Wiki.expandOrCollapse = ($section) => {
    const $header = $section.find('header');
    const sectionId = $section.attr('id');
    const hashStr = window.location.hash.substr(1);

    $header.on('click', () => {
        const $container = $section.next('.section');
        if ($section.hasClass('collapsed')) {
            $section.removeClass('collapsed');
            $container.removeClass('collapsed');
            latex.checkOverflow($container);
        } else {
            $section.addClass('collapsed');
            $container.addClass('collapsed');
        }
    });

    const expandContainer = () => {
        $header.trigger('click');
        _.delay(() => {
            Anchors.scrollToTarget($header);
        }, 100);
    };

    if (isMobile && hashStr === sectionId) {
        expandContainer();
    }
};


/** Handles summary events */
Wiki.summary = function () {
    // expand section if there is only one
    const $skillHeadings = $('.skill-heading');
    if (isMobile && $skillHeadings.length === 1) {
        _.defer(() => {
            $skillHeadings.find('header').trigger('click');
        });
    }

    // highlight citation
    const highlightCitation = () => {
        const { hash } = window.location;
        if (hash.substr(1, 9) === 'citation-') {
            jqGetElemById(hash.substr(1)).addClass('highlight-citation');
        }
    };

    highlightCitation();

    $('.citation-link').on('click', () => {
        _.defer(highlightCitation);
    });
};


/**
 * Rate wiki
 * @param {JQuery} $form
 */
Wiki.rateWiki = function ($form) {
    var $stars = $form.find('li');
    var rating = $form.data('rating');
    var isSubmitting = false;

    if( rating ) {
        $stars.find('input[value=' + rating + ']').parents('li').addClass('selected');
    } else {
        $stars.on('click', function () {
            var $this = $(this);

            if( !isSubmitting ) {
                isSubmitting = true;

                // uncheck default
                $stars.find('input:checked').prop('checked', false);

                $this.addClass('selected');
                $this.find('input').prop('checked', true);
                $form.submit();
            }
        });
    }
};

/**
 * Report disputed wiki as correct
 * @param {JQuery} $form
 */
Wiki.reportDisputedWiki = function ($form) {
    var $submit = $form.find('#submit');
    $submit.on('click', function () {
        $form.submit();
        $('.disputed-container').addClass('hide');
    });
};


/**
 * Shows confirmation popup on /__edit_logs page
 * @param {JQuery} $btn
 */
Wiki.confirmModeratorAction = ($btn) => {
    $btn.on('click', () => {
        const data = $btn.data();
        const $input = $(`#${data.logId}`);
        const requiresConfirm = jqGetElemById('wiki-logs-confirm').prop('checked');

        if (requiresConfirm) {
            showConfirmationModal({
                title: data.confirmModalTitle,
                text: data.confirmModalText,
            }).then((confirmed) => {
                if (confirmed) {
                    $input.val(data.value);
                    $btn.parents('form').submit();
                }
            });
        } else {
            $input.val(data.value);
            $btn.parents('form').submit();
        }
    });
};


/**
 * Paginates /__edit_logs page
 * @param {JQuery} $btn
 */
Wiki.logsPager = function ($btn) {
    $btn.on('click', function() {
        var page = $btn.data('page');
        $('#pagination_page_id').val( page );
        $btn.parents('form').submit();
    });
};

/**
 * Show staff actions on desktop
 * @param {JQuery} $btn
 */
Wiki.showStaffActions = function ($btn) {
    var $actionContainer = $('.wiki-staff-container');
    var $overlay = $('.wiki-overlay');

    // show practice
    $btn.on('click', function (e) {
        e.stopPropagation();
        $actionContainer.show();
        $overlay.show();
    });

    // hide practice when clicking outside practice container
    $('body').on('click', function (e) {
        $actionContainer.hide();
        $overlay.hide();
    });

    $actionContainer.on('click', function (e) {
        e.stopPropagation();
    });
};


/**
 * Show/hide wiki feedback section
 * @param  {JQuery} $container
 */
Wiki.feedback = ($container) => {
    const $btn = jqGetElemById('wiki-feedback-trigger');
    let isActive = $container.hasClass(CLASS_NAME_ACTIVE);

    const toggleActive = (e, preventScroll) => {
        isActive = !isActive;
        if (isActive) {
            if (preventScroll !== true) {
                Anchors.scrollToTarget($container);
            }
            $container.find('.cmp-deferred').each((i, elem) => {
                deferred.load($(elem));
            });
        }
        $container.toggleClass(CLASS_NAME_ACTIVE, isActive);
    };

    $btn.on('click', toggleActive);

    if (window.location.hash.indexOf('feedback') !== -1) {
        toggleActive(null, true);
    }
};


/**
 * Controller.
 * Updates citation with current time from browser instead of relying on Django.
 * @param  {JQuery} $container
 */
Wiki.getCitationTime = ($container) => {
    const tmpl = _.template(jqGetElemById('retrieval-time-content').text());
    const now = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
        'September', 'October', 'November', 'December'];

    $container.find('.retrieval-time').html(tmpl({
        hour: (`0${now.getHours()}`).slice(-2),
        minute: (`0${now.getMinutes()}`).slice(-2),
        month: months[now.getMonth()],
        day: now.getDate(),
        year: now.getFullYear(),
    }));
};


/**
 * Controller.
 * @param {JQuery} $footer
 */
Wiki.wikiFooter = ($footer) => {
    const footerHeight = jqGetElemById('footer').outerHeight();
    if (footerHeight) {
        _.defer(() => {
            let lastDiff = 0;
            $window.on('scroll', _.throttle(() => {
                // calculate all of this every time in case the height changes
                const diff = $window.scrollTop() -
                    (getScrollHeight($body) - footerHeight - $window.height());
                if (diff > 0) {
                    $footer.css({ transform: `translate(0, -${diff}px)` });
                } else if (lastDiff > 0) {
                    $footer.css({ transform: 'translate(0, 0)' });
                }
                lastDiff = diff;
            }, 16));
        });
    }

    const axId = $footer.data('ax-id');
    if (axId) {
        _.defer(() => {
            track('ui', {
                element_id: axId,
                path: window.location.pathname,
                full_path: window.location.pathname + window.location.search,
            });
        });
    }
};


Wiki.showSignUpModal = function ($footer) {
    const $modal = jqGetElemById('signup-modal-wiki');
    const numViewsKey = 'numLoggedOutWikiViews';
    const numViewsToShowSignUpModal = 2;
    let numViews = wafer.get(numViewsKey);

    const isOneStep = $modal.data('one-step') === true;

    const openSignUpModal = () => {
        $modal.modal('show');

        if (isOneStep) {
            // Need to skip the buttons for the 1-step modal
            $modal.find('#signup-email').trigger('click');
        }

        const $modalTourContainer = $modal.find('.public-signup-left');
        if ($modalTourContainer.length && !$modalTourContainer.data('already-opened')) {
            $modalTourContainer.data('already-opened', true);
            $modalTourContainer.attr('data-already-opened', true);

            track('ui', {
                action: 'viewed_prenux_slide',
                step_position: 1,
                is_first_step: true,
                is_last_step: false,
            });
        }
    };

    if (!numViews) {
        wafer.set(numViewsKey, 1);
    } else {
        numViews++;
        wafer.set(numViewsKey, numViews);
    }

    if (numViews >= numViewsToShowSignUpModal) {
        _.delay(openSignUpModal, 3000);
    }

    $footer.find('.signup-btn').on('click', (e) => {
        e.preventDefault();
        openSignUpModal();
    });
};

Wiki.examplesContribModal = function ($modal) {
    $modal.modal();
};

/**
 * Expands/collapses "recommended courses and practice" section
 * @param {JQuery} $sidebar
 */
Wiki.expandableSidebar = function ($sidebar) {
    var $header = $sidebar.find('.header');
    $header.on('click', function () {
        $sidebar.toggleClass('expanded');
    });
};


/**
 * Controller.
 * @param {JQuery} $sidebar
 */
Wiki.sidebarAfterSummary = ($sidebar) => {
    const $summary = jqGetElemById('section-pre-header-section');
    $sidebar.insertAfter($summary).removeClass('hide');
};


Wiki.editLog = function ($container) {
    setInterval(function () {
        var log_entry_ids = [];

        $container.find('#log_entry_list li.unreviewed').each(function (index){
            log_entry_ids.push($(this).data('log_entry_id'));
        });

        if (log_entry_ids.length > 0) {
            $.ajax({
                url: '/wiki/__edit_logs/check_status/',
                type: 'POST',
                data: {'log_entry_ids': log_entry_ids},
                dataType: 'json',
                success: function (j) {
                    var component_url, params;
                    j.reviewed_ids.forEach(function (reviewed_id, index){
                        console.log('Reviewed:', reviewed_id);
                        component_url = '/wiki/__edit_logs/' + reviewed_id + '/';
                        params = {param_key: $('li[data-log_entry_id="' + reviewed_id + '"]').data('param_key')};
                        ajax('wiki_all_edit_log').get(component_url, params);
                    });
                }
            });
        }
    }, 1000);
};


module.exports = Wiki;
